
import { cmToInches } from "@/utils/familyProfile";
import { GrowthChart } from "./GrowthChart";
import { createChartConfig } from "./chart/ChartConfig";

interface HeightChartProps {
  heightData: any[];
  displayOriginalHeight: number | null;
  useMetricHeight: boolean;
  handleHeightUnitToggle: () => void;
  babyAgeInMonths: number;
}

// Average baby height data (in cm) by month
// These are approximate values based on WHO growth charts
const averageHeightData = [
  { month: 0, value: 50 },    // Birth
  { month: 1, value: 54.5 },  // 1 month
  { month: 2, value: 58 },    // 2 months
  { month: 3, value: 61 },    // 3 months
  { month: 4, value: 63.5 },  // 4 months
  { month: 6, value: 67.5 },  // 6 months
  { month: 9, value: 72 },    // 9 months
  { month: 12, value: 76 },   // 12 months
  { month: 15, value: 79 },   // 15 months
  { month: 18, value: 82 },   // 18 months
  { month: 24, value: 87 },   // 24 months (2 years)
  { month: 30, value: 91 },   // 30 months
  { month: 36, value: 95 }    // 36 months (3 years)
];

// Chart configuration
const chartConfig = createChartConfig(
  "height",
  "Height",
  {
    light: "#4f46e5", // Indigo (original color)
    dark: "#818cf8"
  }
);

export function HeightChart({ 
  heightData, 
  displayOriginalHeight, 
  useMetricHeight, 
  handleHeightUnitToggle,
  babyAgeInMonths
}: HeightChartProps) {
  const heightUnitLabel = useMetricHeight ? "cm" : "inches";
  
  // Function to get average height for current age in months
  const getAverageHeightForAge = (ageMonths: number) => {
    // Find the closest data point
    let closestPoint = averageHeightData[0];
    
    for (const point of averageHeightData) {
      if (Math.abs(point.month - ageMonths) < Math.abs(closestPoint.month - ageMonths)) {
        closestPoint = point;
      }
    }
    
    return useMetricHeight ? closestPoint.value : cmToInches(closestPoint.value);
  };

  // Convert average data to current unit
  const convertedAverageData = averageHeightData.map(point => ({
    month: point.month,
    value: useMetricHeight ? point.value : cmToInches(point.value)
  }));

  return (
    <GrowthChart
      title="Height Growth"
      description="Track your baby's height progress over time"
      chartData={heightData}
      displayOriginalValue={displayOriginalHeight}
      useMetricUnit={useMetricHeight}
      handleUnitToggle={handleHeightUnitToggle}
      unitLabel={heightUnitLabel}
      averageData={convertedAverageData}
      getAverageValueForAge={getAverageHeightForAge}
      valueKey="displayHeight"
      chartConfig={chartConfig}
      babyAgeInMonths={babyAgeInMonths}
    />
  );
}

